
import React from 'react';
import { Brand } from '../types';
import { ArrowRight, Store, Check } from 'lucide-react';
import { asArray } from '../utils/asArray';

interface BrandSelectionProps {
  brands: Brand[];
  onSelect: (brandName: string) => void;
  selectedBrands: string[]; // Still an array for compatibility, but will contain max 1 item
  onNext: () => void;
  onBack: () => void;
}

export const BrandSelection: React.FC<BrandSelectionProps> = ({ brands, onSelect, selectedBrands, onNext, onBack }) => {
  const safeBrands = asArray(brands);
  const safeSelected = asArray(selectedBrands);
  
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-8">
         <div>
            <h2 className="text-3xl font-bold text-zinc-900 tracking-tight">Select Brand</h2>
            <p className="text-zinc-500 text-sm mt-1">Please select the brand you are submitting for.</p>
         </div>
         {safeSelected.length > 0 && (
             <div className="text-sm font-bold text-teal-700 bg-teal-50 px-4 py-2 rounded-full border border-teal-100">
                Selected: {safeSelected[0]}
             </div>
         )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
        {safeBrands.map((brand) => {
          const isSelected = safeSelected.includes(brand.name);
          return (
            <button
              key={brand.id}
              onClick={() => onSelect(brand.name)}
              className={`
                group relative flex flex-col items-center justify-center p-6 h-40 rounded-3xl border-2 transition-all duration-300 text-center overflow-hidden
                ${isSelected 
                  ? 'bg-white border-teal-500 shadow-xl shadow-teal-500/10 scale-[1.02]' 
                  : 'bg-white border-transparent hover:border-teal-100 text-zinc-600 shadow-sm hover:shadow-lg hover:shadow-zinc-200/50 hover:-translate-y-1'
                }
              `}
            >
              {isSelected && (
                  <div className="absolute top-3 right-3 animate-fade-in z-20">
                      <div className="bg-teal-500 rounded-full p-1">
                          <Check className="w-3 h-3 text-white" strokeWidth={4} />
                      </div>
                  </div>
              )}

              <div className={`
                w-20 h-20 mb-4 rounded-2xl flex items-center justify-center overflow-hidden transition-colors border
                ${isSelected ? 'bg-teal-50 border-teal-100' : 'bg-zinc-50 border-zinc-100 group-hover:bg-white'}
              `}>
                {brand.logo ? (
                    <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain" />
                ) : (
                    <Store className={`w-8 h-8 ${isSelected ? 'text-teal-600' : 'text-zinc-300 group-hover:text-teal-400'}`} />
                )}
              </div>
              
              <div className="relative z-10 w-full px-2">
                  <span className={`font-bold text-sm leading-tight block truncate ${isSelected ? 'text-teal-900' : 'text-zinc-700'}`}>
                    {brand.name}
                  </span>
              </div>
            </button>
          );
        })}
      </div>
      
      {/* Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-white/80 backdrop-blur-lg border-t border-zinc-200/60 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
           <button 
              onClick={onBack}
              className="px-6 py-3 rounded-2xl text-zinc-600 font-bold hover:bg-zinc-100 transition-colors"
           >
              Back
           </button>
           
           <button
              onClick={onNext}
              disabled={safeSelected.length === 0}
              className={`
                  flex items-center px-8 py-4 text-base font-bold rounded-full shadow-lg transition-all
                  ${safeSelected.length === 0 
                      ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed shadow-none' 
                      : 'bg-zinc-900 text-white hover:bg-zinc-800 hover:scale-105 active:scale-95 shadow-zinc-900/20'
                  }
              `}
           >
              Continue
              <ArrowRight className="ml-2 h-5 w-5" />
           </button>
        </div>
      </div>
      {/* Spacer for fixed footer */}
      <div className="h-24" />
    </div>
  );
};
